
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { Copyright, AlertTriangle, CheckCircle, Search, FileText, ExternalLink, Scale } from 'lucide-react';
import { supabase } from "@/integrations/supabase/client";
import { useNodes } from "@/hooks/useNodes";

const getRiskColor = (risk: string) => {
  switch (risk) {
    case 'low': return 'bg-white text-green-800';
    case 'medium': return 'bg-white text-black';
    case 'high': return 'bg-white text-black';
    case 'critical': return 'bg-red-100 text-red-800';
    default: return 'bg-white text-black';
  }
};

interface AttributionItem {
  id: string;
  node_id: string;
  source_title?: string;
  source_author?: string;
  source_url?: string;
  source_publication?: string;
  license_type?: string;
  copyright_notice?: string;
  attribution_type: string;
  permission_granted?: boolean;
  verification_status?: string;
}

const CopyrightScoringEngine: React.FC = () => {
  const [contentToAnalyze, setContentToAnalyze] = useState('');
  const [selectedNodeId, setSelectedNodeId] = useState('');
  const [analysisResult, setAnalysisResult] = useState<any | null>(null);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();
  const { data: nodes, isLoading: loadingNodes, error: nodesError } = useNodes();

  // For node selection, pull attributions from Supabase
  const analyzeNodeCopyright = async () => {
    if (!selectedNodeId) {
      toast({
        title: "No Node Selected",
        description: "Please select a node to analyze.",
        variant: "destructive",
      });
      return;
    }
    setLoading(true);
    setAnalysisResult(null);
    try {
      const { data, error } = await supabase
        .from('content_attribution')
        .select('*')
        .eq('node_id', selectedNodeId);

      if (error) throw error;

      const items: AttributionItem[] = data ?? [];

      // Fake a coverage metric for demo (e.g., attribution count / total ~5)
      const attrCoverage = Math.min(100, Math.round((items.length / 5) * 100));

      const missingAttribution = items.filter(a => !a.permission_granted || a.verification_status !== "verified");
      let risk_level = 'low';
      if (missingAttribution.length > 1) risk_level = 'medium';
      if (missingAttribution.length > 2) risk_level = 'high';
      if (items.length === 0) risk_level = 'critical';

      setAnalysisResult({
        attribution_coverage: attrCoverage,
        risk_level,
        attributions: items,
        overall_score: Math.max(40, attrCoverage - 5 * missingAttribution.length),
        recommendations: [
          ...(items.length === 0
            ? ["No attribution found. Add copyright and license information for all quoted or referenced material."]
            : []),
          ...missingAttribution.map(a =>
            `Verify attribution for "${a.source_title || "(Untitled)"}" or check license permissions.`
          )
        ]
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || String(error),
        variant: "destructive",
      });
    }
    setLoading(false);
  };

  // Simple fallback for "paste content" (uses mock; could be upgraded to an edge function)
  const analyzePastedContent = async () => {
    if (!contentToAnalyze.trim()) {
      toast({
        title: "No Content",
        description: "Please enter content to analyze",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    setAnalysisResult(null);
    // Simulated delay & findings
    setTimeout(() => {
      setAnalysisResult({
        attribution_coverage: 60,
        risk_level: "medium",
        attributions: [],
        overall_score: 65,
        recommendations: [
          "Check for quotes and add sources.",
          "Review content for image/citation attribution."
        ]
      });
      setLoading(false);
      toast({
        title: "Analysis Complete (Demo)",
        description: "Paste-content analysis uses a simulated engine.",
      });
    }, 900);
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Copyright className="h-5 w-5" />
            Copyright Scoring Engine
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Analyze by Node or Paste Content</Label>
            <div className="flex flex-col gap-2">
              {loadingNodes ? (
                <div className="text-sm text-black">Loading nodes…</div>
              ) : (
                <select
                  value={selectedNodeId}
                  onChange={e => {
                    setSelectedNodeId(e.target.value);
                    setAnalysisResult(null);
                    setContentToAnalyze("");
                  }}
                  className="border rounded px-3 py-2"
                >
                  <option value="">Select Node</option>
                  {nodes?.map((n: any) => (
                    <option key={n.id} value={n.id}>{n.title}</option>
                  ))}
                </select>
              )}

              <div className="relative mt-2">
                <Textarea
                  placeholder="Or paste content here for direct analysis..."
                  value={contentToAnalyze}
                  onChange={e => {
                    setContentToAnalyze(e.target.value);
                    setSelectedNodeId("");
                    setAnalysisResult(null);
                  }}
                  rows={6}
                />
                {selectedNodeId && (
                  <div className="absolute right-2 top-2 text-black text-xs">
                    Using content from selected node
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="flex gap-2">
            <Button
              onClick={analyzeNodeCopyright}
              disabled={loading || !selectedNodeId}
              variant="default"
              className="grow"
            >
              <Search className="h-4 w-4 mr-2" />
              Analyze Selected Node
            </Button>
            <Button
              onClick={analyzePastedContent}
              disabled={loading || !contentToAnalyze.trim()}
              variant="outline"
              className="grow"
            >
              <Search className="h-4 w-4 mr-2" />
              Analyze Pasted Content
            </Button>
          </div>

          {loading && (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <span className="animate-spin text-blue-500"><Scale className="h-4 w-4" /></span>
                <span className="text-sm font-medium">Analyzing...</span>
              </div>
              <Progress value={65} className="w-full" />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Results from DB-based analysis */}
      {analysisResult && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                <Scale className="h-5 w-5" />
                Copyright Analysis Results
              </span>
              <div className="flex items-center gap-2">
                <Badge className={getRiskColor(analysisResult.risk_level)}>
                  {String(analysisResult.risk_level).toUpperCase()} RISK
                </Badge>
                <span className="text-xl font-bold">
                  {analysisResult.overall_score}/100
                </span>
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex flex-col gap-2 md:flex-row md:gap-10">
              <div>
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4" /> Attribution Coverage
                </div>
                <Progress value={analysisResult.attribution_coverage} className="h-2 mt-1" />
                <span className="text-sm">{analysisResult.attribution_coverage}%</span>
              </div>
            </div>

            {/* Attribution list */}
            {analysisResult.attributions?.length > 0 && (
              <div>
                <h4 className="font-medium mb-2 flex items-center gap-2">
                  <CheckCircle className="h-4 w-4" />
                  Attributions ({analysisResult.attributions.length})
                </h4>
                <ul className="space-y-2">
                  {analysisResult.attributions.map((a: AttributionItem, idx: number) => (
                    <li key={a.id || idx} className="border rounded px-3 py-2">
                      <div>
                        <span className="font-medium">{a.source_title || "(Untitled Source)"}</span>
                        {a.source_author && <span className="text-xs ml-2">by {a.source_author}</span>}
                        {a.license_type && (
                          <Badge className="ml-2">{a.license_type}</Badge>
                        )}
                        <div className="text-xs text-black">
                          {a.source_publication}
                          {a.source_url && (
                            <span className="ml-2">
                              <a href={a.source_url} target="_blank" className="underline">
                                Source Link
                              </a>
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="mt-1 text-xs">
                        Attribution type: <b>{a.attribution_type}</b>
                        {a.permission_granted && (
                          <span className="ml-2 text-green-600">Permission Granted</span>
                        )}
                        {a.verification_status && (
                          <Badge
                            className={a.verification_status === "verified"
                              ? "bg-white text-green-800 ml-2"
                              : "bg-white text-black ml-2"}
                          >
                            {a.verification_status}
                          </Badge>
                        )}
                      </div>
                      {a.copyright_notice && (
                        <div className="mt-1 text-xs italic">{a.copyright_notice}</div>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Recommendations */}
            {analysisResult.recommendations?.length > 0 && (
              <div>
                <h4 className="font-medium mb-2 flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-black" />
                  Recommendations
                </h4>
                <ul className="list-disc ml-6">
                  {analysisResult.recommendations.map((rec: string, idx: number) => (
                    <li key={idx} className="text-sm">{rec}</li>
                  ))}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default CopyrightScoringEngine;

